import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AidFoneAssistant } from '../components/landing';
import { localeFromPath } from '../i18n/path-locale';
import WaitlistSection, { WAITLIST_ANCHOR } from '../components/landing/waitlist/WaitlistSection';
import { TRIAL_HREF } from '../config/prelaunch';

// Bilingual production landing — ported from the locked designs
// AidFone_Landing_{EN,FR}_PROD_2026-05-24.html. Serves both "/" (EN) and
// "/fr" (FR); locale is derived from the URL. The old LandingPageV5 is
// retired from routing but left in the repo.
//
// IMPORTANT (client rule): the on-device voice feature is "voice assistant"
// / "Assistant vocal" — NEVER "AI". Only the dashboard chatbot is an
// "AI Assistant". Do not reintroduce "AI"/"IA" into the voice-feature copy.
//
// SEO meta is injected per route by scripts/prerender.js (not React).

type Locale = 'en' | 'fr';

const CANADA_FLAG = (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="16" viewBox="0 0 9600 4800" role="img" aria-label="Canada" className="flex-shrink-0 mt-[3px]">
    <path fill="#d52b1e" d="m0 0h2400l99 99h4602l99-99h2400v4800h-2400l-99-99h-4602l-99 99H0z" />
    <path fill="#fff" d="m2400 0h4800v4800h-4800zm2490 4430-45-863a95 95 0 0 1 111-98l859 151-116-320a65 65 0 0 1 20-73l941-762-212-99a65 65 0 0 1-34-79l186-572-542 115a65 65 0 0 1-73-38l-105-247-423 454a65 65 0 0 1-111-57l204-1052-327 189a65 65 0 0 1-91-27l-332-652-332 652a65 65 0 0 1-91 27l-327-189 204 1052a65 65 0 0 1-111 57l-423-454-105 247a65 65 0 0 1-73 38l-542-115 186 572a65 65 0 0 1-34 79l-212 99 941 762a65 65 0 0 1 20 73l-116 320 859-151a95 95 0 0 1 111 98l-45 863z" />
  </svg>
);

const Check = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e07b4c" strokeWidth="2.5" strokeLinecap="round" className="flex-shrink-0">
    <path d="M5 13l4 4L19 7" />
  </svg>
);

interface Card { icon: string; iconClass: string; cat: string; title: string; items: React.ReactNode[]; }
interface Profile { name: string; count: string; desc: string; }
interface Reason { title: string; body: React.ReactNode; }
interface Step { n: string; time: string; title: string; body: React.ReactNode; }
interface Stage { label: string; img: string; alt: string; }
// `a` is the ordered list of answer paragraphs (v5.8 Q2/Q6 have two).
// ReactNode rather than string so FR can carry a real <sup>MC</sup> node.
interface Faq { q: string; a: React.ReactNode[]; }

interface LocaleContent {
  title: string;
  htmlLang: string;
  navActive: Locale;
  /** Small utility link in the nav for returning users with accounts. */
  loginLabel: string;
  hero: { eyebrow: string; headline: React.ReactNode; leads: string[]; ctaPrimary: string; ctaSecondary: string; founder: React.ReactNode; };
  profiles: { eyebrow: string; heading: React.ReactNode; lead: React.ReactNode; items: Profile[]; cta: string; };
  refusal: { heading: string; lead: React.ReactNode; reasons: Reason[]; cta: string; };
  features: { eyebrow: string; heading: React.ReactNode; cards: Card[]; };
  steps: { eyebrow: string; heading: string; lead: string; items: Step[]; cta: string; };
  evolves: { eyebrow: string; heading: string; sub?: string; exampleTag: string; stages: Stage[]; closing: React.ReactNode; };
  /**
   * `caption` is the visible line under the photo; `photoAlt` is the image's own
   * alt text. They are deliberately separate — the v5.8 masters give the GIF a
   * different alt from the caption, and sharing one field silently rewrote the
   * alt whenever the caption was synced.
   */
  founder: { heading: string; quotes: string[]; name: string; role: string; caption: string; photoAlt: string; };
  faq: { heading: string; items: Faq[]; };
  finalCta: { heading: React.ReactNode; sub?: string; cta: string; note: string; trust: string[]; };
  footer: { tagline: string; links: { label: string; href: string }[]; privacy: { label: string; href: string }; copyright: string; };
}

// Layout preview images for the "Evolves" section, keyed by locale.
// FR variants are designer-localized renders from the Figma master file
// (same dimensions/crop as EN — drop-in compatible).
const PHONES: Record<Locale, { pa_1: string; pc_2: string; vi_2: string }> = {
  en: {
    pa_1: '/images/layouts/figma/pa_1.png',
    pc_2: '/images/layouts/figma/pc_2.png',
    vi_2: '/images/layouts/figma/vi_2.png',
  },
  fr: {
    pa_1: '/images/layouts/figma/fr/pa_1.png',
    pc_2: '/images/layouts/figma/fr/pc_2.png',
    vi_2: '/images/layouts/figma/fr/vi_2.png',
  },
};

const CONTENT: Record<Locale, LocaleContent> = {
  en: {
    title: 'AidFone — A phone Mom or Dad can finally use',
    htmlLang: 'en',
    navActive: 'en',
    loginLabel: 'Login',
    hero: {
      eyebrow: "For parents living with memory loss, Alzheimer's, Parkinson's, tremors, low vision, or macular degeneration.",
      headline: <>A phone <span className="text-[#e07b4c] italic font-semibold">Mom or Dad</span> can finally use.</>,
      leads: [
        'Memory loss? Low vision? Unsteady hands? We built layouts for each.',
        'AidFone turns a recent Android into a simple phone — that you set up for your parent, and manage from your own phone, wherever you are.',
        'For them, it’s just a phone. For you, it’s peace of mind.',
      ],
      ctaPrimary: 'Try 30 Days Free →',
      ctaSecondary: 'How AidFone Works →',
      founder: <>Designed in Canada by an expert in geriatric care — <em className="text-[#1a2e3b] font-medium not-italic">for his own mother.</em></>,
    },
    profiles: {
      eyebrow: 'One app. Many layouts.',
      heading: <>You adapt the phone to <span className="text-[#e07b4c] italic">their abilities</span>.</>,
      lead: <>You're worried because mom or dad needs help. AidFone has layouts for memory, physical and visual needs — choose the right one, change it anytime.</>,
      items: [
        { name: 'Memory & Confusion', count: '5 layouts', desc: 'Mild to moderate loss — memory, attention or learning capacity.' },
        { name: 'Physical', count: '5 layouts', desc: 'Tremors, stiffness, loss of sensation or dexterity.' },
        { name: 'Visual', count: '3 layouts', desc: 'Macular degeneration, cataracts, glaucoma, low vision.' },
      ],
      cta: 'See layouts →',
    },
    refusal: {
      heading: 'Will my parent use it?',
      lead: <>AidFone was built specifically for them. Nothing to swipe. No tiny buttons. No cluttered screens. <strong className="text-[#1a2e3b]">When the anxiety goes, the refusal usually goes with it.</strong></>,
      reasons: [
        { title: 'YOU configure, not your parent.', body: <>We guide you through every step.</> },
        { title: 'Almost nothing for them to learn.', body: <>Tap a face, tap a button — that’s it. Settings stay locked; you manage them from your dashboard.</> },
      ],
      cta: 'Try 30 Days Free →',
    },
    features: {
      eyebrow: 'What AidFone does',
      heading: <>What it does — <span className="text-[#e07b4c] italic">at a glance</span>.</>,
      cards: [
        { icon: '📞', iconClass: 'bg-[#dbeaf5] text-[#1e5f8a]', cat: 'Communication', title: 'Reaching you, made simple.', items: ['They call from a dial pad or from large buttons', 'Or they tap "ASK" and tell their voice assistant what they want'] },
        { icon: '🛡', iconClass: 'bg-[#d1e8d7] text-[#3a7a44]', cat: 'Protection', title: 'Helps you keep watch. Even at 3am.', items: ['911 button — press and hold (on certain layouts)', 'Last-known location — when you turn it on', 'Suspicious calls screened before the phone rings', 'Low battery alert comes to you — not them'] },
        { icon: '♥', iconClass: 'bg-[#fde8dd] text-[#e07b4c]', cat: 'Daily Support', title: 'Their medication routine. Reminders you set, from anywhere.', items: ['Medication reminders you set and update remotely from your dashboard'] },
      ],
    },
    steps: {
      eyebrow: 'How it works',
      heading: 'Your 3 steps to get connected.',
      lead: 'We guide you the whole way.',
      items: [
        { n: '1', time: '2 min', title: 'Subscribe', body: <>Click <strong className="text-[#1a2e3b] font-semibold">"Try 30 Days Free."</strong> No charge for 30 days. Cancel anytime.</> },
        { n: '2', time: '15 min', title: 'Configure', body: <>Answer a few questions online. Select the layout. Add their contacts.</> },
        { n: '3', time: "When you're ready", title: 'Install on their Android', body: <>You receive the activation code, the link, and the guide by email. <strong className="text-[#1a2e3b] font-semibold">No action needed from your parent.</strong></> },
      ],
      cta: 'Try 30 Days Free →',
    },
    evolves: {
      eyebrow: 'Built to evolve',
      heading: 'AidFone grows with your parent',
      sub: 'AidFone keeps evolving. Updates are included in your plan.',
      exampleTag: 'Example',
      stages: [
        { label: 'Maybe today', img: PHONES.en.pa_1, alt: 'AidFone PA1 layout for an independent senior: 911 button, photos shortcut, large dial pad, Contact button, and ASK voice assistant button.' },
        { label: 'Maybe later', img: PHONES.en.pc_2, alt: 'AidFone PC2 layout: 911 button, photos shortcut, five named contact buttons including family members and CLSC, ASK voice assistant, and END button.' },
        { label: 'Maybe much later', img: PHONES.en.vi_2, alt: 'AidFone VI2 layout: two large photo contact tiles and a large ASK voice assistant button.' },
      ],
      closing: <>AidFone is designed to walk with your loved one <span className="text-[#e07b4c]">stage after stage</span>.</>,
    },
    founder: {
      heading: 'Designed by a caregiver.',
      quotes: [
        '"I have 10 years of experience in geriatric care — from home care and assisted living to long-term care and memory care units. I thought I\'d seen it all."',
        '"Then my 93-year-old mother — who still drives and manages her own banking — found herself unable to use a smartphone. I tried various options, but nothing worked. So I built AidFone."',
      ],
      name: 'Normand',
      role: 'Founder & CEO · Montreal, Canada',
      caption: 'Céline, 93 — the reason AidFone exists.',
      photoAlt: 'Céline, 93, using AidFone on her smartphone.',
    },
    faq: {
      heading: 'Frequently asked questions',
      items: [
        {
          q: 'What is AidFone?',
          a: [
            'AidFone is an app that turns a recent Android phone into an ultra-simple phone for seniors, with safety-assist features designed to support caregivers. You set it up for your parent, then manage everything remotely from your own phone.',
          ],
        },
        {
          q: 'Will my parent really use AidFone?',
          a: [
            '“Resistance to change means the senior doesn’t want new ‘gadgets’ in their daily routine. Even after two years of trying, I still haven’t convinced my mother to use a simple pill organizer. Yet she adopted AidFone immediately, because she had already used a cell phone and a cordless phone at home. And her AidFone is easier to use than her old landline.” (Normand, CEO)',
            'For those who dread new technology, AidFone is built to remove what causes the anxiety: everything is simple and clean, with one fixed home screen (dial pad or large buttons), an emergency call system, and a voice assistant. And it’s free for 30 days.',
          ],
        },
        {
          q: 'How does AidFone take some of the load off my shoulders?',
          a: [
            'AidFone acts like a presence at your parent’s side: the emergency call system, scam-call blocking (optional), GPS location, and the AidFone Activity Sentinel™, which helps spot sudden changes — you receive an alert when that happens. At AidFone, we keep adding features to your parent’s phone designed to improve their safety and your peace of mind.',
          ],
        },
        {
          q: 'What is the AidFone Activity Sentinel?',
          a: [
            'The AidFone Activity Sentinel™ lives on your dashboard. Twice a day — one morning window, one evening window — it counts the calls your parent makes and compares that rhythm to their recent habits. If activity drops sharply, you receive a text: a discreet signal to check in. The goal is to spot a change in your parent’s calling habits. Note that AidFone doesn’t track incoming calls and records no numbers, no names, no call durations, and no conversations — you never see a call list. The feature is activated only with your parent’s consent and can be turned off at any time.',
          ],
        },
        {
          q: 'Is AidFone compatible with iPhones?',
          a: [
            "The app installed on your parent's phone is compatible only with Android smartphones. However, as a caregiver, you can access your AidFone dashboard from an Android phone, an iPhone, or a computer running Windows or macOS.",
          ],
        },
        {
          q: 'How can I protect an aging parent from phone scams?',
          a: [
            'By default, AidFone blocks calls from numbers that aren’t on the contact list you approved. Calls from numbers you haven’t approved — unknown callers, robocalls, fake government agents, grandparent scams — go quietly to voicemail instead of ringing your parent’s phone. AidFone doesn’t intercept or record audio. Your dashboard simply shows that a voicemail is waiting, so you can tell your parent to listen to it.',
            'If your parent is alert and has no cognitive loss, this option may not be necessary. You can then turn it off from your dashboard. Your parent then receives their calls as usual.',
          ],
        },
        {
          q: 'What happens if my parent has a medical emergency?',
          a: [
            'There is one way to trigger an emergency call, and it keeps them in control. On layouts that include the 911 button, it works in two steps: your parent presses and holds it, then a 10-second “Calling 911” screen appears with two large buttons — CALL NOW and CANCEL. If they don’t touch anything, the phone places the 911 call on its own.',
          ],
        },
        {
          q: 'How much does AidFone cost?',
          a: [
            "$10.99 per month, plus tax, after a 30-day free trial (credit card required at signup). No activation fees. The subscription includes the app on your parent's Android phone, with updates, plus unlimited caregiver dashboard access. AidFone sells no phones — use a recent Android you already have at home, or a basic new model. Cancel anytime — no charge.",
          ],
        },
      ],
    },
    finalCta: {
      heading: <>Help them keep their independence.<br />Give yourself the breathing room you deserve.</>,
      sub: 'Start your free trial today.',
      cta: 'Try 30 Days Free',
      note: "During your trial, you'll receive an email 7 days before billing begins. Easy online cancellation.",
      trust: ['Credit card required — no charge for 30 days', 'French & English support'],
    },
    footer: {
      tagline: 'Be there for them. From anywhere.',
      links: [
        { label: 'Features', href: '#features' },
        { label: 'How it works', href: '#how-it-works' },
        { label: 'FAQ', href: '#faq' },
        { label: 'Contact', href: 'mailto:support@aidfone.com' },
      ],
      privacy: { label: 'Privacy', href: '/privacy-policy' },
      copyright: '© 2026 AidFone Inc. Built in Canada 🍁',
    },
  },
  fr: {
    title: 'AidFone — Le téléphone intelligent pour aînés',
    htmlLang: 'fr-CA',
    navActive: 'fr',
    loginLabel: 'Connexion',
    hero: {
      eyebrow: "Pour les parents vivant avec une perte d'autonomie physique, l'Alzheimer, le Parkinson, des pertes de mémoire, la DMLA, des cataractes ou une basse vision.",
      headline: <>Le téléphone intelligent <span className="text-[#e07b4c] italic font-semibold">pour aînés</span>.</>,
      leads: [
        'Pertes de mémoire? Mains tremblantes? Baisse de la vision? AidFone a des modèles d’écran pour toutes ces conditions.',
        'AidFone est une application qui transforme un cellulaire Android récent en un téléphone ultra-simple. Vous le configurez pour votre parent, puis vous le gérez à distance, depuis votre propre téléphone.',
        'Pour eux, c’est un téléphone. Pour vous, c’est du répit.',
      ],
      ctaPrimary: 'Essai gratuit de 30 jours →',
      ctaSecondary: 'Comment AidFone fonctionne →',
      founder: <>Conçu au Canada par un expert en soins gériatriques, <em className="text-[#1a2e3b] font-medium not-italic">pour sa propre mère.</em></>,
    },
    profiles: {
      eyebrow: 'Une application. Plusieurs interfaces.',
      heading: <>Vous adaptez le téléphone à <span className="text-[#e07b4c] italic">leurs capacités</span>.</>,
      lead: <>Anxiété et épuisement, c'est souvent ce qui arrive quand on a un parent qui a de la difficulté à nous contacter, qui est à risque de chute, et qui a un besoin accru d'assistance. AidFone a des modèles d'écran pour les pertes de mémoire, les limitations physiques et la basse vision — choisissez le bon, changez-le au besoin.</>,
      items: [
        { name: 'Mémoire et confusion', count: '5 modèles', desc: 'Pertes légères à modérées (mémoire, attention ou apprentissage).' },
        { name: 'Physique', count: '5 modèles', desc: 'Tremblements, raideurs, perte de sensation ou de dextérité.' },
        { name: 'Visuel', count: '3 modèles', desc: 'Dégénérescence maculaire, cataractes, glaucome, basse vision.' },
      ],
      cta: 'Voir les modèles d’écran →',
    },
    refusal: {
      heading: 'Mon parent va-t-il l’utiliser?',
      lead: <>AidFone a été conçu pour les personnes âgées vivant avec une perte d’autonomie légère à modérée. La technologie s’adapte très rarement à elles. <strong className="text-[#1a2e3b]">AidFone l’a fait.</strong> L’écran a été simplifié pour que votre mère ou votre père soit à l’aise et en confiance avec son téléphone.</>,
      reasons: [
        { title: 'VOUS configurez l’appareil pour votre parent.', body: <>Nous vous guidons étape par étape.</> },
        { title: 'Presque rien à apprendre pour eux.', body: <>AidFone s’adapte à des capacités d’apprentissage et de mémoire variées. Peu de choses à apprendre. Les réglages sont verrouillés; vous les gérez depuis votre tableau de bord.</> },
      ],
      cta: 'Essai gratuit de 30 jours →',
    },
    features: {
      eyebrow: 'Ce que fait AidFone',
      heading: <>Plus qu’un simple <span className="text-[#e07b4c] italic">téléphone</span>.</>,
      cards: [
        { icon: '📞', iconClass: 'bg-[#dbeaf5] text-[#1e5f8a]', cat: 'Communication', title: 'Vous joindre, simplement.', items: ["Pour les appels, il y a des interfaces avec clavier, d'autres avec de grosses touches, avec ou sans la photo du proche aidant", "Ou encore ils appuient sur la touche « PARLER » et demandent à leur Assistant vocal d'initier l'appel pour eux"] },
        { icon: '🛡', iconClass: 'bg-[#d1e8d7] text-[#3a7a44]', cat: 'Protection', title: 'Vous aide à veiller sur eux.', items: ["Bouton 9-1-1 — à maintenir enfoncé (sur certains modèles)", 'Dernière position connue — si vous activez cette option', 'Appels suspects filtrés avant que le téléphone sonne', 'Batterie faible? Vous recevez une notification sur votre cellulaire'] },
        { icon: '♥', iconClass: 'bg-[#fde8dd] text-[#e07b4c]', cat: 'Soutien quotidien', title: 'Rappel vocal pour la prise de médicaments.', items: ["Depuis votre tableau de bord, vous entrez les heures de prise de médicaments et l'Assistant vocal avise votre mère ou père", 'Vous ajustez ces heures au besoin'] },
      ],
    },
    steps: {
      eyebrow: 'Comment ça marche',
      heading: 'Vos 3 étapes pour être connectés.',
      lead: 'Nous vous accompagnons à chaque étape.',
      items: [
        { n: '1', time: '2 min', title: 'Abonnez-vous', body: <>Cliquez sur <strong className="text-[#1a2e3b] font-semibold">«&nbsp;Essai gratuit de 30 jours&nbsp;»</strong>. Rien à payer pendant 30 jours. Annulez n’importe quand.</> },
        { n: '2', time: '15 min', title: 'Configurez', body: <>Répondez à quelques questions en ligne. Choisissez le modèle d’écran. Ajoutez leurs contacts.</> },
        { n: '3', time: 'À votre rythme', title: 'Installez sur leur Android', body: <>Vous recevez par courriel le code d’activation, le lien et le guide. <strong className="text-[#1a2e3b] font-semibold">Aucune intervention de votre parent n’est requise.</strong></> },
      ],
      cta: 'Essai gratuit de 30 jours →',
    },
    evolves: {
      eyebrow: 'Conçu pour évoluer',
      heading: 'AidFone évolue avec votre parent',
      sub: 'AidFone continue d’évoluer. Les mises à jour sont incluses dans votre abonnement.',
      exampleTag: 'Exemple',
      stages: [
        { label: 'Peut-être aujourd’hui', img: PHONES.fr.pa_1, alt: "Écran AidFone PA1 pour aîné autonome : bouton 9-1-1, raccourci Photos, grand clavier numérique, bouton Contact et bouton PARLER pour l'Assistant vocal." },
        { label: 'Peut-être plus tard', img: PHONES.fr.pc_2, alt: 'Écran AidFone PC2 : bouton 9-1-1, raccourci Photos, cinq boutons nommés incluant des membres de la famille et CLSC, Assistant vocal PARLER et bouton FIN.' },
        { label: 'Peut-être beaucoup plus tard', img: PHONES.fr.vi_2, alt: "Écran AidFone VI2 : deux grandes tuiles photo de contacts et un grand bouton PARLER pour l'Assistant vocal." },
      ],
      closing: <>AidFone est conçu pour accompagner votre proche en s’adaptant à sa condition, <span className="text-[#e07b4c]">une étape à la fois</span>.</>,
    },
    founder: {
      heading: 'Conçu par un proche aidant.',
      // Guillemets carry U+00A0 on the inside, exactly as the v5.8 master renders
      // them (  escapes so the non-breaking space stays visible in review).
      quotes: [
        '« J’ai 10 ans d’expérience en soins gériatriques — des soins à domicile aux résidences pour aînés, en passant par les CHSLD et les unités de soins cognitifs. Je pensais avoir fait le tour. »',
        '« Puis, ma mère de 93 ans, qui conduit et gère encore ses finances, s’est retrouvée incapable d’utiliser un téléphone intelligent. J’ai essayé plusieurs options, rien n’a fonctionné. J’ai donc créé AidFone. »',
      ],
      name: 'Normand',
      role: 'Fondateur et PDG · Montréal, Canada',
      caption: 'Céline, 93 ans, la raison d’être d’AidFone.',
      photoAlt: 'Céline, 93 ans, utilisant AidFone sur son téléphone intelligent.',
    },
    faq: {
      heading: 'Foire aux questions',
      items: [
        {
          q: 'AidFone, c’est quoi?',
          a: [
            'AidFone est une application qui transforme un cellulaire Android récent en un téléphone ultra-simple pour les aînés, avec des fonctionnalités d’aide à la sécurité qui visent à soutenir les proches aidants. Vous le configurez pour votre parent, puis vous le gérez à distance, depuis votre propre téléphone.',
          ],
        },
        {
          q: 'Mon parent va-t-il vraiment utiliser AidFone?',
          a: [
            '« La résistance au changement, c’est l’aîné qui ne veut pas de nouveaux “gadgets” dans son quotidien. Même après deux ans de tentatives, je n’ai toujours pas réussi à convaincre ma mère d’utiliser un simple pilulier. Elle a pourtant adopté AidFone immédiatement, parce qu’elle avait déjà utilisé un cellulaire et un téléphone sans fil à la maison. Et son AidFone est plus simple à utiliser que son vieux téléphone de table. » (Normand, CEO)',
            'AidFone est conçu pour réduire la confusion : l’interface est simple et épurée, avec un écran d’accueil fixe (clavier ou grands boutons), un système d’appel d’urgence et un Assistant vocal. Et c’est gratuit pendant 30 jours.',
          ],
        },
        {
          q: 'Comment AidFone peut-il alléger ma charge de proche aidant?',
          a: [
            <>AidFone agit comme une présence aux côtés de votre parent : le système d’appel d’urgence, le blocage d’appels anti-fraudeurs (optionnel), la localisation GPS, mais aussi le Veilleur d’activité AidFone<sup>MC</sup>, qui permet de repérer des changements soudains — une alerte vous est envoyée lorsque cela se produit. Chez AidFone, on travaille à ajouter des fonctionnalités pour améliorer la sécurité de votre parent et vous offrir du répit.</>,
          ],
        },
        {
          q: 'Qu’est-ce que le Veilleur d’activité AidFone?',
          a: [
            <>Le Veilleur d’activité AidFone<sup>MC</sup> se trouve sur votre tableau de bord. Deux fois par jour — une plage le matin, une le soir — il compte les appels effectués par votre parent et compare ce rythme à ses habitudes récentes. Si l’activité baisse de façon marquée, vous recevez un texto : un signal discret pour prendre des nouvelles. L’objectif est de repérer un changement dans les habitudes d’appel. À noter qu’AidFone ne compile pas les appels entrants et n’enregistre aucun numéro, aucun nom, aucune durée d’appel ni aucune conversation — vous ne voyez jamais une liste d’appels. La fonction s’active seulement avec le consentement de votre parent et se désactive en tout temps.</>,
          ],
        },
        {
          q: 'AidFone est-il compatible avec les iPhone?',
          a: [
            'L’application installée sur le téléphone de votre parent est compatible uniquement avec les appareils Android. Par contre, comme proche aidant, vous accédez à votre tableau de bord AidFone depuis un Android, un iPhone, ou un ordinateur Windows ou macOS.',
          ],
        },
        {
          q: 'Comment protéger un parent âgé des fraudes téléphoniques?',
          a: [
            'Par défaut, AidFone bloque les appels des numéros qui ne figurent pas dans la liste de contacts que vous avez approuvée. Les appels provenant de numéros non approuvés — numéros inconnus, appels automatisés, faux agents gouvernementaux, fraudes visant les grands-parents — vont discrètement à la boîte vocale au lieu de faire sonner le téléphone de votre parent. AidFone n’intercepte ni n’enregistre l’audio. Votre tableau de bord montre simplement qu’un message vocal est en attente, pour que vous puissiez dire à votre parent de l’écouter.',
            'Si votre parent est alerte et n’a aucune perte cognitive, cette option n’est peut-être pas nécessaire. Vous pouvez alors la désactiver depuis votre tableau de bord. Votre parent recevra alors ses appels comme d’habitude.',
          ],
        },
        {
          q: 'Que se passe-t-il si mon parent a une urgence médicale?',
          a: [
            'Le déclenchement d’un appel d’urgence est simple, laissant le contrôle à votre parent. Sur les modèles d’écran incluant le bouton 911, ça se fait en deux étapes : votre parent le maintient enfoncé, puis un écran de confirmation de 10 secondes apparaît avec deux grands boutons, APPELER MAINTENANT et ANNULER. S’il ne touche à rien, le téléphone lance automatiquement l’appel au 911.',
          ],
        },
        {
          q: 'Combien coûte AidFone?',
          a: [
            '10,99 $ par mois, taxes en sus, après l’essai gratuit de 30 jours (carte de crédit requise à l’inscription). Pas de frais d’activation. L’abonnement inclut l’application sur le téléphone Android de votre parent, les mises à jour, et l’accès illimité à votre tableau de bord. AidFone ne vend pas de téléphones — utilisez un téléphone Android récent que vous avez déjà à la maison ou un modèle neuf de base. Annulez n’importe quand, sans frais.',
          ],
        },
      ],
    },
    finalCta: {
      heading: <>Aidez-les à garder leur autonomie.<br />Offrez-vous le répit que vous méritez.</>,
      sub: 'Commencez votre essai gratuit aujourd’hui.',
      cta: 'Essai gratuit de 30 jours',
      note: 'Durant l’essai, vous recevrez un courriel 7 jours avant le premier paiement. Annulation facile en ligne.',
      trust: ['Carte de crédit requise — rien à payer pendant 30 jours', 'Soutien en français et en anglais'],
    },
    footer: {
      tagline: 'Présent pour eux. De n’importe où.',
      links: [
        { label: 'Fonctionnalités', href: '#features' },
        { label: 'Comment ça marche', href: '#how-it-works' },
        { label: 'FAQ', href: '#faq' },
        { label: 'Contact', href: 'mailto:support@aidfone.com' },
      ],
      privacy: { label: 'Confidentialité', href: '/fr/politique-de-confidentialite' },
      copyright: '© 2026 AidFone Inc. Conçu au Canada 🍁',
    },
  },
};

export const LandingV6: React.FC = () => {
  const location = useLocation();
  const locale = localeFromPath(location.pathname);
  const c = CONTENT[locale];
  const homeHref = locale === 'fr' ? '/fr' : '/';
  const { i18n } = useTranslation();

  useEffect(() => {
    document.title = c.title;
    document.documentElement.lang = c.htmlLang;
  }, [c.title, c.htmlLang]);

  // Sync i18next + localStorage to the URL-derived locale. Without this,
  // clicking the FR toggle changes the landing copy (URL-driven) but
  // i18next stays at its initial-detection value — so downstream routes
  // that don't have a /fr prefix (/configure, /login, /dashboard) read
  // the stale i18n.language and render in the wrong language.
  // localStorage persists the choice so a fresh tab to /configure inherits.
  useEffect(() => {
    if (!i18n.language?.startsWith(locale)) {
      i18n.changeLanguage(locale);
    }
    try { localStorage.setItem('aidfone_language', locale); } catch { /* private mode */ }
  }, [locale, i18n]);

  const liClass = "py-[5px] pl-4 relative before:content-['·'] before:text-[#e07b4c] before:absolute before:left-[5px] before:font-bold before:text-[18px] before:leading-none";

  return (
    <div className="bg-[#FAF6EF] text-[#1A2B47] font-body leading-normal">
      {/* ========== NAV ========== */}
      <nav className="fixed top-0 left-0 right-0 z-[1000] bg-[#FAF6EF]/95 backdrop-blur-md border-b border-[#e5e1dc] py-[14px]">
        <div className="max-w-[1100px] mx-auto px-6 flex items-center justify-between">
          <Link to={homeHref} className="flex items-center">
            <img src="/images/landing/aidfone-logo-horizontal.png" alt="AidFone" className="h-[38px] w-auto" width={540} height={121} />
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/login"
              className="text-xs font-medium text-[#5a6d7a] hover:text-[#1a2e3b] no-underline transition-colors"
            >
              {c.loginLabel}
            </Link>
            <div className="inline-flex bg-[#f0ebe3] rounded-full p-[3px]">
              <Link to="/" className={`px-[11px] py-[5px] rounded-full text-xs font-semibold no-underline transition-colors ${c.navActive === 'en' ? 'bg-white text-[#1a2e3b] shadow-[0_1px_2px_rgba(0,0,0,0.08)]' : 'text-[#7a8a98] hover:text-[#1a2e3b]'}`}>EN</Link>
              <Link to="/fr" className={`px-[11px] py-[5px] rounded-full text-xs font-semibold no-underline transition-colors ${c.navActive === 'fr' ? 'bg-white text-[#1a2e3b] shadow-[0_1px_2px_rgba(0,0,0,0.08)]' : 'text-[#7a8a98] hover:text-[#1a2e3b]'}`}>FR</Link>
            </div>
          </div>
        </div>
      </nav>

      <main className="pt-[72px]">
        {/* ========== HERO ========== */}
        <section className="py-[50px] pb-10">
          <div className="max-w-[880px] mx-auto px-6">
            <p className="text-[11.5px] text-[#1A2B47]/70 leading-[1.6] mb-[14px] tracking-[0.02em]">{c.hero.eyebrow}</p>
            <h1 className="font-display text-[clamp(28px,5vw,42px)] font-bold text-[#1a2e3b] leading-[1.1] mb-[18px] tracking-[-0.5px]">{c.hero.headline}</h1>
            {c.hero.leads.map((lead, i) => (
              <p key={i} className="text-[15px] leading-[1.6] text-[#3a4a58] mb-3 max-w-[680px]">{lead}</p>
            ))}
            <div className="flex flex-wrap gap-[10px] mb-[22px] mt-2">
              <Link to={TRIAL_HREF} className="inline-block bg-[#e07b4c] text-white px-6 py-[13px] rounded-lg text-[14.5px] font-semibold no-underline shadow-[0_3px_12px_rgba(224,123,76,0.32)]">{c.hero.ctaPrimary}</Link>
              <a href="#how-it-works" className="inline-block bg-transparent text-[#1A2B47] border-[1.5px] border-[#c0d4e8] px-[22px] py-3 rounded-lg text-[14.5px] font-medium no-underline">{c.hero.ctaSecondary}</a>
            </div>
            <div className="flex items-start gap-[10px] text-[13px] text-[#5a6d7a] leading-[1.5] pt-[18px] border-t border-[#e5e1dc] max-w-[680px]">
              {CANADA_FLAG}
              <span>{c.hero.founder}</span>
            </div>
          </div>
        </section>

        <WaitlistSection locale={locale} anchorId={WAITLIST_ANCHOR} />

        {/* ========== S3 — PROFILES ========== */}
        <section id="profiles" className="bg-white border-t border-b border-[#e5e1dc] py-[60px]">
          <div className="max-w-[880px] mx-auto px-6">
            <p className="text-xs font-bold tracking-[0.12em] uppercase text-[#e07b4c] mb-2">{c.profiles.eyebrow}</p>
            <h2 className="font-display text-[clamp(24px,3.5vw,30px)] font-bold text-[#1a2e3b] leading-[1.18] mb-[14px] tracking-[-0.3px]">{c.profiles.heading}</h2>
            <p className="text-[14.5px] leading-[1.65] text-[#3a4a58] mb-[22px]">{c.profiles.lead}</p>
            {c.profiles.items.map((p) => (
              <div key={p.name} className="bg-[#FAF6EF] border border-[#e5e1dc] rounded-[10px] p-4 mb-3">
                <div className="flex items-center gap-[10px] flex-wrap mb-1.5">
                  <span className="font-display text-[17px] font-semibold text-[#1a2e3b]">{p.name}</span>
                  <span className="bg-[#e07b4c] text-white text-[10.5px] font-bold tracking-[0.08em] uppercase px-[9px] py-[3px] rounded-full">{p.count}</span>
                </div>
                <div className="text-[13px] text-[#5a6d7a] leading-[1.5]">{p.desc}</div>
              </div>
            ))}
            <Link to={TRIAL_HREF} className="inline-block bg-transparent text-[#1A2B47] border-[1.5px] border-[#1A2B47] px-5 py-[10px] rounded-lg text-[13.5px] font-semibold mt-2 no-underline">{c.profiles.cta}</Link>
          </div>
        </section>

        {/* ========== S5 — WILL MY PARENT USE IT ========== */}
        <section className="py-[60px]">
          <div className="max-w-[880px] mx-auto px-6">
            <h2 className="font-display text-[clamp(24px,3.5vw,30px)] font-bold text-[#1a2e3b] leading-[1.18] mb-[14px] tracking-[-0.3px]">{c.refusal.heading}</h2>
            <p className="text-[14.5px] leading-[1.65] text-[#3a4a58] mb-[22px]">{c.refusal.lead}</p>
            <ul className="m-0 mb-[22px] p-0 list-none">
              {c.refusal.reasons.map((r) => (
                <li key={r.title} className="mb-[14px] pl-[18px] border-l-2 border-[#e07b4c]">
                  <div className="font-display text-[16px] font-semibold text-[#1a2e3b] mb-1 leading-[1.3]">{r.title}</div>
                  <p className="text-[14px] text-[#3a4a58] leading-[1.5] m-0">{r.body}</p>
                </li>
              ))}
            </ul>
            <Link to={TRIAL_HREF} className="inline-block bg-[#e07b4c] text-white px-[22px] py-[13px] rounded-lg text-[14.5px] font-semibold no-underline shadow-[0_3px_12px_rgba(224,123,76,0.32)]">{c.refusal.cta}</Link>
          </div>
        </section>

        {/* ========== S7 — WHAT AIDFONE DOES ========== */}
        <section id="features" className="bg-white border-t border-b border-[#e5e1dc] py-[60px]">
          <div className="max-w-[880px] mx-auto px-6">
            <p className="text-xs font-bold tracking-[0.12em] uppercase text-[#e07b4c] mb-2">{c.features.eyebrow}</p>
            <h2 className="font-display text-[clamp(24px,3.5vw,30px)] font-bold text-[#1a2e3b] leading-[1.18] mb-[14px] tracking-[-0.3px]">{c.features.heading}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {c.features.cards.map((card) => (
                <div key={card.cat} className="bg-[#FAF6EF] border border-[#e5e1dc] rounded-xl p-[22px]">
                  <div className={`w-10 h-10 rounded-[10px] flex items-center justify-center mb-3 text-[18px] ${card.iconClass}`}>{card.icon}</div>
                  <p className="text-[11px] font-bold tracking-[0.14em] uppercase text-[#7a8a98] mb-[5px]">{card.cat}</p>
                  <div className="font-display text-[17px] font-semibold text-[#1a2e3b] mb-[14px] leading-[1.25]">{card.title}</div>
                  <ul className="text-[13.5px] text-[#3a4a58] leading-[1.6] m-0 p-0 list-none">
                    {card.items.map((item, i) => (<li key={i} className={liClass}>{item}</li>))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ========== S6 — THREE STEPS ========== */}
        <section id="how-it-works" className="py-[60px]">
          <div className="max-w-[880px] mx-auto px-6">
            <p className="text-xs font-bold tracking-[0.12em] uppercase text-[#e07b4c] mb-2">{c.steps.eyebrow}</p>
            <h2 className="font-display text-[clamp(24px,3.5vw,30px)] font-bold text-[#1a2e3b] leading-[1.18] mb-[14px] tracking-[-0.3px]">{c.steps.heading}</h2>
            <p className="text-[14.5px] leading-[1.65] text-[#3a4a58] mb-[22px]">{c.steps.lead}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-[22px]">
              {c.steps.items.map((s) => (
                <div key={s.n} className="bg-white border border-[#e5e1dc] rounded-[10px] px-4 py-[22px] text-center">
                  <div className="w-[46px] h-[46px] rounded-full bg-[#1A2B47] text-white flex items-center justify-center font-display text-[20px] font-bold mx-auto mb-3 shadow-[0_0_0_4px_rgba(224,123,76,0.18)]">{s.n}</div>
                  <div className="text-[11px] text-[#e07b4c] font-bold tracking-[0.1em] uppercase mb-1.5">{s.time}</div>
                  <div className="font-display text-[15px] font-semibold text-[#1a2e3b] mb-2">{s.title}</div>
                  <div className="text-[13px] text-[#5a6d7a] leading-[1.5]">{s.body}</div>
                </div>
              ))}
            </div>
            <Link to={TRIAL_HREF} className="block text-center bg-[#e07b4c] text-white px-6 py-[14px] rounded-lg text-[15px] font-semibold shadow-[0_3px_12px_rgba(224,123,76,0.32)] no-underline max-w-[340px] mx-auto">{c.steps.cta}</Link>
          </div>
        </section>

        {/* ========== S9 — EVOLVES ========== */}
        <section className="bg-white border-t border-b border-[#e5e1dc] py-[60px]">
          <div className="max-w-[880px] mx-auto px-6">
            <p className="text-xs font-bold tracking-[0.12em] uppercase text-[#e07b4c] mb-2 text-center">{c.evolves.eyebrow}</p>
            <h2 className="font-display text-[clamp(24px,3.5vw,30px)] font-bold text-[#1a2e3b] leading-[1.18] mb-[14px] tracking-[-0.3px] text-center">{c.evolves.heading}</h2>
            {c.evolves.sub && <p className="text-[14.5px] text-[#5a6d7a] text-center mb-4">{c.evolves.sub}</p>}
            <div className="text-center mb-7">
              <span className="inline-block bg-[#e07b4c] text-white text-[11px] font-bold tracking-[0.14em] uppercase px-[14px] py-[5px] rounded-full">{c.evolves.exampleTag}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_32px_1fr_32px_1fr] gap-2 items-center mb-7">
              {c.evolves.stages.map((stage, i) => (
                <React.Fragment key={stage.label}>
                  <div className="flex flex-col items-center text-center">
                    <div className="font-display text-[14px] font-semibold italic text-[#1a2e3b] mb-[14px]">{stage.label}</div>
                    <div className="bg-[#1f1f1f] rounded-[24px] p-1.5 shadow-[0_6px_20px_rgba(0,0,0,0.18)] max-w-[180px] w-full">
                      <img src={stage.img} alt={stage.alt} className="w-full h-auto block rounded-[20px]" loading="lazy" />
                    </div>
                  </div>
                  {i < c.evolves.stages.length - 1 && (
                    <div className="text-[#e07b4c] text-[24px] leading-none flex items-center justify-center md:pt-10 rotate-90 md:rotate-0 justify-self-center" aria-hidden="true">→</div>
                  )}
                </React.Fragment>
              ))}
            </div>
            <p className="font-display text-[17px] font-semibold text-[#1a2e3b] border-l-[3px] border-[#e07b4c] py-3 pl-4 m-0 leading-[1.55]">{c.evolves.closing}</p>
          </div>
        </section>

        {/* ========== S10 — FOUNDER STORY ========== */}
        <section className="py-[60px]">
          <div className="max-w-[880px] mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_300px] gap-10 items-center">
              <div>
                <h2 className="font-display text-[clamp(22px,3.5vw,26px)] font-bold text-[#1a2e3b] leading-[1.2] mb-5 tracking-[-0.3px]">{c.founder.heading}</h2>
                <div className="text-[15.5px] leading-[1.75] italic text-[#4a5a68]">
                  {c.founder.quotes.map((q, i) => (<p key={i} className="m-0 mb-[14px]">{q}</p>))}
                </div>
                <div className="mt-5 pt-4 border-t border-[#e5e1dc]">
                  <strong className="text-[15px] text-[#1a2e3b]">{c.founder.name}</strong>
                  <span className="block text-[13px] text-[#7a8a98] mt-1">{c.founder.role}</span>
                </div>
              </div>
              <div>
                <div className="rounded-[14px] overflow-hidden shadow-[0_8px_28px_rgba(0,0,0,0.14)]">
                  <img src="/images/landing/celine-using-aidfone.gif" alt={c.founder.photoAlt} className="w-full h-auto block" width={280} height={498} loading="lazy" />
                </div>
                <p className="mt-2.5 text-[13px] text-[#7a8a98] italic text-center">{c.founder.caption}</p>
              </div>
            </div>
          </div>
        </section>

        {/* ========== S11 — FAQ ========== */}
        <section id="faq" className="bg-white border-t border-[#e5e1dc] py-[60px]">
          <div className="max-w-[880px] mx-auto px-6">
            <h2 className="font-display text-[clamp(24px,3.5vw,30px)] font-bold text-[#1a2e3b] leading-[1.2] mb-[26px] tracking-[-0.3px] text-center">{c.faq.heading}</h2>
            <div className="flex flex-col gap-3">
              {c.faq.items.map((f) => (
                <details key={f.q} className="group bg-[#FAF6EF] border border-[#e5e1dc] rounded-[10px] overflow-hidden open:shadow-[0_2px_12px_rgba(0,0,0,0.06)]">
                  <summary className="font-display text-[16px] font-semibold text-[#1a2e3b] py-[18px] pr-12 pl-5 cursor-pointer leading-[1.35] list-none relative select-none marker:hidden [&::-webkit-details-marker]:hidden after:content-['+'] after:absolute after:right-[18px] after:top-1/2 after:-translate-y-1/2 after:text-[24px] after:font-normal after:text-[#e07b4c] after:leading-none group-open:after:content-['−']">
                    {f.q}
                  </summary>
                  {/* v5.8 answers are an ordered paragraph list (Q2 and Q6 have two).
                      The wrapper keeps the original padding/divider; each extra
                      paragraph is separated with mt-3. Single-paragraph answers
                      render exactly as before. */}
                  <div className="px-5 pb-5 border-t border-[#e5e1dc] pt-4">
                    {f.a.map((para, i) => (
                      <p key={i} className={`text-[14px] text-[#3a4a58] leading-[1.65] m-0${i > 0 ? ' mt-3' : ''}`}>{para}</p>
                    ))}
                  </div>
                </details>
              ))}
            </div>
            <div className="text-center mt-7">
              <Link to={locale === 'fr' ? '/fr/faq' : '/faq'} className="inline-block text-[14.5px] font-semibold text-[#e07b4c] no-underline hover:underline">
                {locale === 'fr' ? 'Voir toutes les questions →' : 'See all questions →'}
              </Link>
            </div>
          </div>
        </section>

        {/* ========== FINAL CTA ========== */}
        <section id="try" className="bg-gradient-to-br from-[#1A2B47] to-[#0f1d35] text-white text-center py-[70px]">
          <div className="max-w-[880px] mx-auto px-6">
            <h2 className="font-display text-[clamp(24px,4vw,32px)] font-semibold text-white m-0 mb-3 leading-[1.2]">{c.finalCta.heading}</h2>
            {c.finalCta.sub && <p className="text-[16px] opacity-85 m-0 mb-[26px]">{c.finalCta.sub}</p>}
            <Link to={TRIAL_HREF} className="inline-block bg-[#e07b4c] text-white px-8 py-[14px] rounded-lg text-[15.5px] font-semibold no-underline shadow-[0_3px_12px_rgba(224,123,76,0.32)]">{c.finalCta.cta}</Link>
            <p className="text-[13px] opacity-70 mt-[18px] max-w-[520px] mx-auto">{c.finalCta.note}</p>
            <div className="flex justify-center gap-5 mt-[22px] flex-wrap text-[13px] opacity-85">
              {c.finalCta.trust.map((t, i) => (<span key={i} className="inline-flex items-center gap-1.5"><Check />{t}</span>))}
            </div>
          </div>
        </section>

        <WaitlistSection locale={locale} />
      </main>

      {/* ========== FOOTER ========== */}
      <footer className="bg-[#1A2B47] text-white/70 pt-10 pb-[30px] text-center">
        <div className="max-w-[1100px] mx-auto px-6">
          <div className="inline-flex items-center gap-[10px] mb-[18px]">
            <img src="/images/landing/aidfone-favicon.png" alt="AidFone" className="w-[26px] h-[26px] object-contain" width={26} height={26} />
            <span className="font-display text-[22px] font-bold text-white">AidFone</span>
          </div>
          <p className="text-[14px] m-0 mb-[18px]">{c.footer.tagline}</p>
          <div className="flex flex-wrap justify-center gap-[14px] text-[13.5px] mb-6">
            {c.footer.links.map((l) => (
              <a key={l.label} href={l.href} className="text-white/70 no-underline hover:text-white transition-colors">{l.label}</a>
            ))}
            {/* Plain <a>, not React Router <Link>: these legal pages are static HTML
                files served by Netlify, not React routes. A <Link> would client-side
                navigate to /privacy-policy (no route) → NotFound (the launch-blocking 404). */}
            <a href={c.footer.privacy.href} className="text-white/70 no-underline hover:text-white transition-colors">{c.footer.privacy.label}</a>
          </div>
          <p className="text-[12px] opacity-60">{c.footer.copyright}</p>
        </div>
      </footer>

      <AidFoneAssistant visitorType="prospect" />
    </div>
  );
};

export default LandingV6;
